/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { Icon } from 'sarsaparilla';
import * as MapHelper from 'shared-ui/maps/helpers/mapHelper';
import { format } from 'date-fns';

import { formatItemName, formatLocationName } from '../utils/stringUtils';
import * as types from '../constants';

const propTypes = {
    trip: PropTypes.object,
    items: PropTypes.array,
    destinationIndex: PropTypes.number,
    onTripItemSelected: PropTypes.func,
    onBookTripItem: PropTypes.func,
    onDeleteTripItem: PropTypes.func,
    editable: PropTypes.bool,
    arrivalDate: PropTypes.any,
    departureDate: PropTypes.any,
    tripId: PropTypes.string,
};

export default class TripItineraryItems extends React.Component {
    static propTypes = propTypes;

    onItemDelete(item) {
        if (this.props.onDeleteTripItem) {
            this.props.onDeleteTripItem(this.props.destinationIndex, item);
        }
    }

    onItemBook(item, tripId, arrivalDate, departureDate) {
        const data = {
            ...item,
            tripId,
            arrivalDate: format(arrivalDate, types.FNS_DATE_FORMAT),
            departureDate: format(departureDate, types.FNS_DATE_FORMAT),
        };

        if (this.props.onBookTripItem) {
            this.props.onBookTripItem(this.props.destinationIndex, data);
        }
    }

    renderIcon(item) {
        if (item.entity_type === 'campground') {
            return (
                <div className="trip-planner-itinerary-item-icon-campground">
                    <Icon iconName="tent" />
                </div>
            );
        }
        if (item.entity_type === 'tour' || item.entity_type === 'ticketfacility') {
            return (
                <div className="trip-planner-itinerary-item-icon-tour">
                    <Icon iconName="ticket" />
                </div>
            );
        }
        if (item.entity_type === 'timedentry_tour' || item.entity_type === 'timedentry') {
            return (
                <div className="trip-planner-itinerary-item-icon-tour">
                    <Icon iconName="ticket" />
                </div>
            );
        }
        if (item.entity_type === 'permit') {
            return (
                <div className="trip-planner-itinerary-item-icon-permit">
                    <Icon iconName="card" />
                </div>
            );
        }
        if (item.entity_type === 'vehiclepermit') {
            return (
                <div className="trip-planner-itinerary-item-icon-permit">
                    <Icon iconName="card" />
                </div>
            );
        }
        if (item.entity_type === 'recarea') {
            return (
                <div className="trip-planner-itinerary-item-icon-rec-area">
                    <Icon iconName="tree" />
                </div>
            );
        }
        if (item.entity_type === 'activitypass') {
            return (
                <div className="trip-planner-itinerary-item-icon-rec-area">
                    <Icon iconName="card" />
                </div>
            );
        }
        if (item.entity_type === 'dayuse') {
            return (
                <div className="trip-planner-itinerary-item-icon-day-use">
                    <Icon iconName="picnic-table" />
                </div>
            );
        }
        if (item.entity_type === 'poi') {
            return (
                <div className="trip-planner-itinerary-item-icon-poi">
                    <Icon iconName="compass" />
                </div>
            );
        }

        return (
            <div className="trip-planner-itinerary-item-icon">
                <Icon iconName="bird-watching" />
            </div>
        );
    }

    renderItems() {
        if (this.props.items) {
            return this.props.items.map((item, index) => {
                if (!item.entity_id) {
                    return null;
                }

                const targetUri = MapHelper.getTargetUri(item);
                const name = formatItemName(item);

                let actionButton = null;

                if (this.props.trip.start_date) {
                    if (item.reserved) {
                        actionButton = (
                            <button
                                type="button"
                                className="rec-button-secondary"
                                onClick={this.onItemBook.bind(
                                    this,
                                    item,
                                    this.props.tripId,
                                    this.props.arrivalDate,
                                    this.props.departureDate
                                )}
                            >
                                {item.status}
                            </button>
                        );
                    } else if (item.reservable) {
                        if (item.availability === 'unavailable') {
                            actionButton = (
                                <button
                                    type="button"
                                    className="rec-button-tertiary"
                                    onClick={this.onItemBook.bind(this, item)}
                                >
                                    Unavailable
                                </button>
                            );
                        } else {
                            actionButton = (
                                <button
                                    type="button"
                                    className="rec-button-primary"
                                    onClick={this.onItemBook.bind(
                                        this,
                                        item,
                                        this.props.tripId,
                                        this.props.arrivalDate,
                                        this.props.departureDate
                                    )}
                                >
                                    Book Now
                                </button>
                            );
                        }
                    }
                }

                let countDesc = null;

                if (!item.reserved) {
                    if (item.reservable && item.campsites_count) {
                        let desc = 'sites';
                        if (item.campsites_count === 1) {
                            desc = 'site';
                        }
                        countDesc = (
                            <ul className="rec-pill-wrap">
                                <li>
                                    <Link
                                        to={`${targetUri}/campsites`}
                                        className="search-campground-sites"
                                    >
                                        <strong>
                                            {item.campsites_count} {desc}
                                        </strong>
                                    </Link>
                                </li>
                            </ul>
                        );
                    }
                }

                return (
                    <div key={item.entity_id} className="trip-planner-itinerary-item">
                        {this.renderIcon(item)}
                        <div className="trip-planner-itinerary-item-location">
                            <button
                                type="button"
                                className="rec-button-link"
                                onClick={this.props.onTripItemSelected.bind(
                                    this,
                                    this.props.destinationIndex,
                                    index
                                )}
                            >
                                {name}
                            </button>
                            <ul className="rec-card-meta rec-v-fill-space">
                                {(!item.reservable ||
                                    item.entity_type === 'tour' ||
                                    item.entity_type === 'permit' ||
                                    item.entity_type === 'vehiclepermit') &&
                                    item.parent_name &&
                                    item.parent_name.length > 0 && (
                                        <li>{formatLocationName(item.parent_name)}</li>
                                    )}
                                {(!item.reservable ||
                                    item.entity_type === 'tour' ||
                                    item.entity_type === 'permit' ||
                                    item.entity_type === 'vehiclepermit') &&
                                    (!item.parent_name ||
                                        !item.parent_name.length === 0) &&
                                    item.org_name &&
                                    item.org_name.length > 0 && (
                                        <li>{formatLocationName(item.org_name)}</li>
                                    )}
                                {item.city && item.state_code && (
                                    <li>
                                        Near {item.city}, {item.state_code}
                                    </li>
                                )}
                            </ul>
                            {countDesc}
                        </div>
                        {this.props.editable && (
                            <div className="trip-planner-itinerary-item-actions">
                                {actionButton}
                                <button
                                    type="button"
                                    className="rec-button-tertiary remove"
                                    aria-label={`Remove ${name}`}
                                    onClick={this.onItemDelete.bind(this, item)}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                );
            });
        }

        return null;
    }

    render() {
        return (
            <div
                className="trip-planner-itinerary-destination-items"
                data-testid="trip-planner-itinerary-destination-items"
            >
                {this.renderItems()}
            </div>
        );
    }
}
